<template>
    <div>
        <div v-if="!comm_log_loaded && !load_error" class="text-center gray-600 py-3">
            <b-spinner small class="mr-2" />로그를 불러오는 중입니다.
        </div>
        <div v-else-if="load_error" class="text-center py-3">
            <div class="text-danger mb-2">로그를 불러오지 못했습니다.</div>
            <b-button size="sm" variant="dark-border" @click="readCompanyCommLog">다시 시도</b-button>
        </div>
        <div v-else-if="commlogs.length === 0" class="text-center gray-600 py-3">등록된 로그가 없습니다.</div>
        <div v-for="item in commlogs"
            :key="item.comment_id || item.uid"
            class="mb-2"
            >
            <div v-if="item.author_id != null" class="f-90 gray-600">
                {{ item.author_name }}, {{item.time_stamp}}
            </div>
            <template v-if="!item.editing">
                <!-- 입력이 평문 textarea 라 출력도 평문으로 — 줄바꿈은 살리고 꺾쇠는 글자 그대로 -->
                <b-card class="px-3 py-2 bgray-200" style="white-space: pre-wrap; word-break: break-word;">{{ displayComment(item.comment) }}</b-card>
            </template>
            <template v-else>
                <b-form-textarea v-model="item.draft"
                    rows="3"
                    :state="item.empty ? false : null"
                    :disabled="item.saving"
                    @input="item.empty = false"
                    />
                <b-form-invalid-feedback :state="item.empty ? false : null">내용을 입력해 주세요.</b-form-invalid-feedback>
            </template>
            <div class="text-right pt-1">
                <template v-if="!item.editing">
                    <b-button variant="dark-border" size="sm" class="mr-2" aria-label="로그 수정" @click="startEdit(item)"><fa icon="pen"></fa></b-button>
                    <b-button variant="dark-border" size="sm" aria-label="로그 삭제" @click="handleDelete(item)"><fa icon="trash-alt"></fa></b-button>
                </template>
                <template v-else>
                    <b-button class="mr-2" size="sm" :disabled="item.saving" @click="cancelEdit(item)">취소</b-button>
                    <b-button variant="blue" size="sm" :disabled="item.saving" @click="handleSave(item)">
                        <b-spinner v-if="item.saving" small class="mr-1" />저장
                    </b-button>
                </template>
            </div>
        </div>

        <b-button variant="blue" class="mb-2" @click="addNewLog">+ 새 로그 추가</b-button>

    </div>
</template>

<script>
import { mapState } from 'vuex'
import { getErrorMessage } from '@core/utils/api/errorMessage'

// 저장 전 새 로그의 목록 key (comment_id 가 아직 없다)
let newLogSeq = 0

export default {
    props: {
        comment_company: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            commlogs: [],
            comm_log_loaded: false,
            load_error: false
        }
    },
    computed:{
        ...mapState('project_code',['is_project_codes_loaded','now_project']),
        ...mapState(['user_id'])

    },
    async created () {
        await this.readCompanyCommLog()
    },
    beforeDestroy(){
        this.comm_log_loaded = false
        this.commlogs = []
    },
    methods:{
        // 화면 전용 편집 상태를 붙인다. 고치는 글은 draft 에 두고 저장에 성공해야 comment 가 바뀐다
        toViewItem (log) {
            return { ...log, editing: false, draft: '', saving: false, empty: false }
        },
        // 예전에 <br> 로 줄을 바꿔 저장한 로그가 있으면 줄바꿈으로 보여 준다
        displayComment (comment) {
            return String(comment ?? '').replace(/<br\s*\/?>/gi, '\n')
        },
        async readCompanyCommLog () {
            const { company_id , project_code , campaign_id } = this.comment_company
            this.load_error = false
            try {
                const response = await this.$axios.get('/read_company_comm_log', {
                    params : {
                        company_id, project_code, campaign_id
                    }
                })
                this.commlogs = response.data.map(this.toViewItem)
                this.comm_log_loaded = true
            } catch (error) {
                this.load_error = true
            }

        },
        startEdit (item) {
            item.draft = this.displayComment(item.comment)
            item.empty = false
            item.editing = true
        },
        cancelEdit (item) {
            if (item.comment_id) {
                item.editing = false
                return
            }
            // 저장 전 새 로그는 목록에서 뺀다
            const at = this.commlogs.indexOf(item)
            if (at > -1) this.commlogs.splice(at, 1)
        },
        async handleSave (item) {
            if (item.saving) return
            if (!item.draft || !item.draft.trim()) {
                item.empty = true
                return
            }
            item.saving = true
            const saved = await this.saveCommLog(item)
            item.saving = false
            // 저장 실패 시 편집 상태를 유지해 입력값을 잃지 않게 한다
            const at = this.commlogs.indexOf(item)
            if (saved && at > -1) this.commlogs.splice(at, 1, saved)
        },
        async saveCommLog (item) {
            let formData = new FormData
            if (item.comment_id) formData.append('comment_id', item.comment_id)
            formData.append('author_id', this.user_id)
            formData.append('comment', item.draft)
            formData.append('company_id', item.company_id)
            formData.append('project_code', item.project_code)
            if (item.campaign_id != null) formData.append('campaign_id', item.campaign_id)

            let response
            try {
                response = await this.$axios.post('/save_company_comm_log', formData)
            } catch (error) {
                if (error?.response?.status === 401) return null
                this.$EventBus.$emit('toast-failure', '저장 실패', getErrorMessage(error, '커뮤니케이션 로그를 저장하지 못했습니다. 입력한 내용은 유지됩니다.'))
                return null
            }
            if (response.data.code === 'success') {
                this.$EventBus.$emit('toast-success', '성공', '커뮤니케이션 로그를 저장하였습니다.')
                return this.toViewItem({
                    comment_id : response.data.comment_id,
                    author_id : response.data.author_id,
                    author_name : response.data.author_name,
                    comment: response.data.comment,
                    company_id: response.data.company_id,
                    project_code: response.data.project_code,
                    campaign_id : response.data.campaign_id,
                    time_stamp : response.data.time_stamp
                })
            }
            this.$EventBus.$emit('toast-failure', '저장 실패', response.data.msg || '커뮤니케이션 로그를 저장하지 못했습니다. 입력한 내용은 유지됩니다.')
            return null
        },
        async handleDelete (item) {
            const is_confirm_delete = await this.$showModal({
                type: 'confirm',
                state: 'error',
                title: '로그 삭제',
                message: `${item.author_name} · ${item.time_stamp} 로그를 삭제합니다. 되돌릴 수 없습니다.`
            })
            if (!is_confirm_delete) return
            let formData = new FormData
            formData.append('comment_id', item.comment_id)
            try {
                const response = await this.$axios.post('/delete_comm_log', formData)
                if (response.data.code === 'success') {
                    this.$EventBus.$emit('toast-success','성공', response.data.msg)
                    const at = this.commlogs.indexOf(item)
                    if (at > -1) this.commlogs.splice(at, 1)
                } else {
                    this.$EventBus.$emit('toast-failure', '삭제 실패', response.data.msg || '로그를 삭제하지 못했습니다.')
                }
            } catch (error) {
                if (error?.response?.status === 401) return
                this.$EventBus.$emit('toast-failure', '삭제 실패', getErrorMessage(error, '로그를 삭제하지 못했습니다.'))
            }
        },
        // 새 로그는 바로 편집 상태로 연다(취소하면 목록에서 빠진다)
        addNewLog(){
            const log = this.toViewItem({
                uid: `new-${++newLogSeq}`,
                company_id: this.comment_company.company_id,
                project_code: this.comment_company.project_code,
                campaign_id: this.comment_company.campaign_id,
                comment: ''
            })
            log.editing = true
            this.commlogs.push(log)
        }

    }
}
</script>

<style>

</style>
